import React from "react";

import { CardWithTitle } from "components/helpers/CardWithTitle/CardWithTitle.jsx";
import { DataTable } from "components/ui/DataTable";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientBaselineHistory } from "hooks/usePatientBaselineHistory";
import {
  BASELINE_METRICS,
  buildSeriesForMetric,
  getMetricIndicator,
} from "utils/baselineTrends";
import { formatDate } from "utils/dateFormatter";

import s from "./BaselineHistory.module.css";
import { BaselineTrendChart } from "./BaselineTrendChart/BaselineTrendChart.jsx";

// Blood pressure is stored on each history record as separate systolic/
// diastolic readings (also how the "Baseline Details" add form now captures
// it), so it's rendered as one combined column here to match how the trend
// charts already read it.
const COLUMNS = [
  {
    id: "created",
    header: "Date",
    // baselineDate is not captured by any form on the frontend, so it's
    // always null — created (server-stamped on every insert) is the one
    // date every record actually has.
    render: (baseline) => formatDate(baseline.created) || "-",
  },
  {
    id: "weightKg",
    header: "Weight (kg)",
    render: (baseline) => baseline.weightKg ?? "-",
  },
  {
    id: "heightCm",
    header: "Height (cm)",
    render: (baseline) => baseline.heightCm ?? "-",
  },
  {
    id: "waistCm",
    header: "Waist (cm)",
    render: (baseline) => baseline.waistCm ?? "-",
  },
  {
    id: "bloodPressure",
    header: "BP (mmHg)",
    render: (baseline) =>
      baseline.systolic && baseline.diastolic
        ? `${baseline.systolic}/${baseline.diastolic}`
        : "-",
  },
  {
    id: "pulse",
    header: "Pulse (bpm)",
    render: (baseline) => baseline.pulse ?? "-",
  },
  {
    id: "cholesterol",
    header: "Cholesterol",
    render: (baseline) => baseline.cholesterol ?? "-",
  },
  { id: "hdl", header: "HDL", render: (baseline) => baseline.hdl ?? "-" },
  { id: "lld", header: "LDL", render: (baseline) => baseline.lld ?? "-" },
];

// Trend history for the Baseline tab — only earns its place once there's
// more than one reading to compare, since a single baseline has nothing to
// trend and is already fully shown by the "Baseline Details" card above it.
const BaselineHistory = () => {
  const patientId = useCurrentPatientId();
  const { data: history, isLoading } = usePatientBaselineHistory(patientId);

  if (isLoading || (history ?? []).length < 2) return null;

  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.created) - new Date(a.created)
  );

  return (
    <div className={s.wrapper}>
      <CardWithTitle title="Baseline History">
        <DataTable columns={COLUMNS} data={sortedHistory} getRowId={(row) => row.id} />
      </CardWithTitle>

      <div className={s.chartsGrid}>
        {BASELINE_METRICS.map((metric) => (
          <BaselineTrendChart
            key={metric.key}
            title={metric.title}
            unit={metric.unit}
            series={buildSeriesForMetric(metric, history)}
            indicator={getMetricIndicator(metric, history)}
          />
        ))}
      </div>
    </div>
  );
};

export { BaselineHistory };
